import { useEffect, useState } from 'react';

/**
 * Live repository facts from the public GitHub API, so the project
 * table shows real last-push dates instead of numbers typed by hand.
 * Unauthenticated, 60 requests an hour per visitor IP, so results are
 * cached for the tab session. Any failure falls back to "unavailable".
 */

const CACHE_KEY = 'repo-meta-v2';

const readCache = () => {
  try {
    return JSON.parse(window.sessionStorage.getItem(CACHE_KEY)) || null;
  } catch {
    return null;
  }
};

const writeCache = (value) => {
  try {
    window.sessionStorage.setItem(CACHE_KEY, JSON.stringify(value));
  } catch {
    /* storage blocked; the page still works without it */
  }
};

const repoPath = (url) => url.replace(/^https:\/\/github\.com\//, '').replace(/\/+$/, '');

const fetchOne = async (url) => {
  const res = await fetch(`https://api.github.com/repos/${repoPath(url)}`, {
    headers: { Accept: 'application/vnd.github+json' }
  });
  if (res.status === 404) return { missing: true };
  if (!res.ok) throw new Error(`GitHub ${res.status}`);
  const repo = await res.json();
  return {
    pushedAt: repo.pushed_at,
    language: repo.language
  };
};

export const useRepoMeta = (urls) => {
  const [meta, setMeta] = useState(() => readCache());
  const key = urls.join('|');

  useEffect(() => {
    if (meta) return undefined;
    let cancelled = false;

    Promise.allSettled(key.split('|').map(fetchOne)).then((results) => {
      if (cancelled) return;
      const next = {};
      key.split('|').forEach((url, i) => {
        next[url] = results[i].status === 'fulfilled' ? results[i].value : null;
      });
      setMeta(next);
      if (Object.values(next).some(Boolean)) writeCache(next);
    });

    return () => {
      cancelled = true;
    };
  }, [key, meta]);

  return meta;
};

export const formatDate = (iso) =>
  new Date(iso).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
